import { api } from '@/app'
import { Organisation } from '@/features'
import { PaginationResponse, QueryManager } from '@/packages'
import { analytics } from '@/services/analytics/segment'
import { TypeGuards } from '@codeleap/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from '../queryClient'
import { BASE_URL } from './constants'
import { organisationQueryKeys } from './queryKeys'

type OrganizationFilters = {
  name?: string
  show_unverified?: boolean
  sort_by?: 'name' | '-min_employees' | '-max_employees'
}

async function getUnknownOrganisation() {
  const response = await api.get<Organisation.T.Organisation>(`${BASE_URL}unknown/`)

  return response.data
}

export const organisationsManager = new QueryManager({
  itemType: {} as Organisation.T.Organisation,
  name: 'organisations',
  queryClient: queryClient.client,

  listItems: async (limit, offset, filters: OrganizationFilters) => {
    const response = await api.get<PaginationResponse<Organisation.T.Organisation>>(BASE_URL, {
      params: {
        limit,
        offset,

        ...filters,
        name: filters.name?.trim() || undefined
      }
    })

    return response.data
  },

  createItem: async data => {
    const response = await api.post(BASE_URL, data)

    return response.data
  },

  retrieveItem: async id => {
    if (TypeGuards.isNil(id)) return undefined
    const response = await api.get<Organisation.T.Organisation>(`${BASE_URL}${id}/`)

    return response.data
  }
})

export const useUnknownOrganisation = () => {
  const query = useQuery({
    queryKey: organisationQueryKeys.unknownOrganisation,
    queryFn: getUnknownOrganisation,
    select: data => {
      organisationsManager.updateItems(data)
      return data
    }
  })

  return {
    query,
    organisation: query.data
  }
}

export function getOrganisationInvolved() {
  return api.get(`${BASE_URL}get_involved/`)
}

export async function contact(organisationId: Organisation.T.Organisation['id']) {
  const response = await api.post(`${BASE_URL}contact/`, {
    organization: organisationId
  })

  return response.data
}

export function useContact(organisation: Organisation.T.Organisation) {
  const mut = useMutation({
    mutationFn: contact
  })

  const onContact = async () => {
    analytics.track('Info_requested', {
      organization: organisation?.name
    })
    mut.mutateAsync(organisation.id)
  }

  return {
    contact: onContact,
    isSuccess: mut.isSuccess,
    isLoading: mut.isPending
  }
}
